import { MouseEvent } from "react";
import styled from "styled-components";
import { canAddCalendarConnection } from "@lifeat/shared/calendar/CalendarUtil";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { useCallToActionContext } from "@contexts/CallToActionContext";
import { useUserContext } from "@contexts/UserContext";
import GoogleLogo from "@resources/logos/integrations/Google/google_logo.png";
import { platform } from "@todesktop/client-core";
import { UpgradeCTASource } from "@user/AmplitudeProSubsEvents";
import { CalendarIntegrationEventSource, StartGoogleCalendarAuthEvent } from "../../AmplitudeCalendarEvents";

const StyledButton = styled(LifeAtButtonV2)`
    & .lifeat-button-content {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        height: auto;
    }
`;

const IconImg = styled.img`
    width: 20px;
    height: auto;
`;

interface GoogleCalendarAuthButtonProps {
    source: CalendarIntegrationEventSource;
    className?: string;
}

function GoogleCalendarAuthButton(props: GoogleCalendarAuthButtonProps) {
    const amplitudeService = AmplitudeService.getInstance();
    const { userData } = useUserContext();
    const { setUpgradeCtaContext } = useCallToActionContext();

    const url = calculateGoogleSignInUrl();

    const handleClick = async (e: MouseEvent) => {
        if (userData == null) {
            e.preventDefault();
            return;
        }

        if (!canAddCalendarConnection(userData)) {
            e.preventDefault();
            setUpgradeCtaContext("multipleCalendars", UpgradeCTASource.CALENDAR_AUTH_BUTTON);
            return;
        }

        amplitudeService.logEvent(new StartGoogleCalendarAuthEvent(props.source));
        if (window.todesktop) {
            e.preventDefault();
            await platform.os.openURL(url);
        }
    };

    return (
        <StyledButton className={props.className} variant="outline" href={url} onClick={handleClick}>
            <IconImg alt={`google_logo`} src={GoogleLogo} />
            <span>Google</span>
        </StyledButton>
    );
}

export default GoogleCalendarAuthButton;

function calculateGoogleSignInUrl() {
    const SCOPES = [
        "email",
        "https://www.googleapis.com/auth/calendar.calendarlist.readonly",
        "https://www.googleapis.com/auth/calendar.events.readonly",
    ];

    const params = new URLSearchParams({
        response_type: "code",
        access_type: "offline",
        redirect_uri: new URL("/oauth/google-calendar/callback", window.location.origin).href,
        prompt: "select_account consent",
        state: JSON.stringify({ shouldGoBackToDesktop: !!window.todesktop }),
        client_id: process.env.GOOGLE_CALENDAR_CLIENT_ID,
        scope: SCOPES.join(" "),
    });

    return `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
}
